import React, { useState, useContext } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { colors } from '../theme/colors';
import { AuthContext } from '../contexts/AuthContext';
import { BannerContext } from '../contexts/BannerContext';
import useApi from '../hooks/useApi';
import LandingNav from '../components/shared/LandingNav';
import Seo from '../components/Seo';

const ContactUs = () => {
  const { user } = useContext(AuthContext);
  const { showMessage } = useContext(BannerContext);
  const { request } = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if user was redirected here for a specific request type
  const requestBetaAccess = location.state?.requestBetaAccess || false;
  
  const [formData, setFormData] = useState({
    email: user?.email || '',
    subject: requestBetaAccess ? 'Account Access Inquiry' : '',
    message: requestBetaAccess ? 'Trading Style: \n\nExperience Level: \n\nFeatures I\'m Most Excited About: \n' : ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify({
          ...formData,
          userId: user?.id,
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          email: user?.email || '',
          subject: '',
          message: ''
        });
        showMessage('Message sent successfully! We\'ll get back to you soon.', 'success');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Contact form error:', errorData);
        setSubmitStatus('error');
        showMessage('Failed to send message. Please try again.', 'danger');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      showMessage('Failed to send message. Please try again.', 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${colors.bgPrimary} 0%, ${colors.bgSecondary} 100%)` }}>
      <Seo
        title="Contact Us | PrecisionTrader"
        description="Get in touch with the PrecisionTrader team for support, account access, or questions about the platform."
        canonicalPath="/contact"
      />
      {/* Navigation */}
      <LandingNav />

      <Container fluid style={{ 
        padding: '40px 20px'
      }}>
        <Row className="justify-content-center">
        <Col lg={8} xl={6}>
          <div style={{
            background: colors.bgSecondary,
            borderRadius: 16,
            padding: '40px',
            border: `1px solid ${colors.borderLight}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            marginTop: '60px'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h1 style={{
                color: colors.text,
                fontSize: '2.5rem',
                fontWeight: 700,
                marginBottom: '16px',
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {requestBetaAccess ? 'Get Started' : 'Get in Touch'}
              </h1>
            </div>

            {/* Important Trading Notice */}
            <div style={{ 
              marginBottom: '32px', 
              padding: '20px', 
              background: 'rgba(0, 0, 0, 0.1)', 
              borderRadius: 8,
              border: `1px solid ${colors.borderLight}`,
              fontSize: '14px',
              color: colors.textMuted
            }}>
              <h4 style={{
                color: '#ef4444',
                fontSize: '1rem',
                fontWeight: 600,
                marginBottom: '12px'
              }}>
                Important Trading Notice
              </h4>
              <p style={{ marginBottom: '12px', lineHeight: 1.5 }}>
                <strong>Emergency Trading Access:</strong> If you're experiencing issues with orders, 
                position management, or any critical trading functions in PrecisionTrader, you can always 
                log into your TradeStation account directly and use their web trading platform for 
                trading needs.
              </p>
              <p style={{ marginBottom: 0, lineHeight: 1.5 }}>
                <strong>Support Response:</strong> We typically respond to messages within 24 hours. 
                For urgent trading issues, please contact TradeStation support directly.
              </p>
            </div>

            {/* Account Access Info Banner */}
            {requestBetaAccess && (
              <Alert style={{
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: 8,
                marginBottom: '32px',
                padding: '20px'
              }}>
                <div style={{ color: colors.text, marginBottom: '12px' }}>
                  <strong style={{ fontSize: '16px' }}>Get Started with PrecisionTrader</strong>
                </div>
                <p style={{ color: colors.textMuted, marginBottom: '12px', lineHeight: 1.6 }}>
                  Tell us about your trading experience and why you're interested in PrecisionTrader. 
                  We'll review your information and help you get started.
                </p>
                <p style={{ color: colors.textMuted, marginBottom: 0, lineHeight: 1.6 }}>
                  <strong>What to include:</strong> Your trading style, experience level, and what 
                  features you're most excited about.
                </p>
              </Alert>
            )}

            {/* Contact Form */}
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ 
                      color: colors.text, 
                      fontWeight: 600, 
                      marginBottom: '8px',
                      fontSize: '14px'
                    }}>
                      Email Address
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      style={{
                        backgroundColor: colors.bgPrimary,
                        border: `1px solid ${colors.borderLight}`,
                        color: colors.text,
                        borderRadius: 8,
                        padding: '12px 16px',
                        fontSize: '16px'
                      }}
                      placeholder="your@email.com"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ 
                      color: colors.text, 
                      fontWeight: 600, 
                      marginBottom: '8px',
                      fontSize: '14px'
                    }}>
                      Subject
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      style={{
                        backgroundColor: colors.bgPrimary,
                        border: `1px solid ${colors.borderLight}`,
                        color: colors.text,
                        borderRadius: 8,
                        padding: '12px 16px',
                        fontSize: '16px'
                      }}
                      placeholder="What's this about?"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ 
                      color: colors.text, 
                      fontWeight: 600, 
                      marginBottom: '8px',
                      fontSize: '14px'
                    }}>
                      Your Message
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      style={{
                        backgroundColor: colors.bgPrimary,
                        border: `1px solid ${colors.borderLight}`,
                        color: colors.text,
                        borderRadius: 8,
                        padding: '12px 16px',
                        fontSize: '16px',
                        resize: 'vertical'
                      }}
                      placeholder="Tell us what's on your mind..."
                    />
                  </Form.Group>
                </Col>
              </Row>

              {submitStatus === 'success' && (
                <Alert variant="success" style={{
                  backgroundColor: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  color: '#22c55e',
                  borderRadius: 8,
                  marginBottom: '24px'
                }}>
                  Message sent successfully! We'll get back to you within 24 hours.
                </Alert>
              )}

              {submitStatus === 'error' && (
                <Alert variant="danger" style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  borderRadius: 8,
                  marginBottom: '24px'
                }}>
                  Failed to send message. Please try again.
                </Alert>
              )}

              <div style={{ textAlign: 'center' }}>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 32px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: 'white',
                    minWidth: '160px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.2)';
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </Form>
            
            <div style={{
              marginTop: '24px',
              padding: 20,
              background: colors.surfaceLight,
              borderRadius: 8,
              border: `1px solid ${colors.borderLight}`,
              fontSize: '13px',
              lineHeight: 1.6,
              color: colors.textMuted
            }}>
              <p style={{ margin: 0 }}>
                <strong>Important Disclosure:</strong> PrecisionTrader is not affiliated or associated with, or a partner of, 
                or an agent, representative, delegate, associated person or introducing broker for, any TradeStation company. 
                The products and services offered by PrecisionTrader are not sponsored, endorsed, sold or promoted by any 
                TradeStation company, and TradeStation makes no representation regarding any products or services offered 
                by PrecisionTrader. Equities, equity options and commodity futures products and services are offered by 
                TradeStation Securities, Inc. (Member NYSE, FINRA, CME and SIPC).
              </p>
            </div>
          </div>
        </Col>
      </Row>
      </Container>
    </div>
  );
};

export default ContactUs;
